/*
Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/

import React, { forwardRef } from 'react';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nextId from 'react-id-generator';
import { GatsbyLink } from '../GatsbyLink';
import '@spectrum-css/menu';
import { CheckMark } from '../Icons';

const Menu = forwardRef(({ children, ...props }, ref) => {
  return (
    <ul ref={ref} className="spectrum-Menu" role="menubar" {...props}>
      {children}
    </ul>
  );
});

const Item = ({ children, isDivider = false, isHighlighted, isSelected, href = '', isHeightUnset, ...props }) => {
  const Element = href ? GatsbyLink : 'li';

  return isDivider ? (
    <li className="spectrum-Menu-divider" role="separator" />
  ) : (
    <Element
      className={classNames('spectrum-Menu-item', { 'is-open': isHighlighted }, { 'is-selected': isSelected })}
      to={href}
      role="menuitem"
      tabIndex="0"
      css={css`
        text-align: left;
      `}
      {...props}>
      <div css={css`display: flex;
    flex-direction: row;
    justify-content: flex-start;
    overflow: hidden;
    align-self: start;
    ${ isHeightUnset && !isHeightUnset ? `margin-top: var(--spectrum-global-dimension-size-50) !important` : ""}
}`}>
        <div css={css`
          width: 20px !important;
         ${ isHeightUnset ? "margin-top: 8px;" : "height: 15px !important; margin-top: 4px;" }
         padding-right: 5px;
         overflow: hidden;
        `}>
          <CheckMark
            className="spectrum-Menu-checkmark spectrum-Menu-itemIcon"
            css={css`
             display: ${isSelected ? "block" : "none"} ;
             color:  var(--spectrum-alias-icon-color-selected) !important;
            `}
          />
          </div>
      </div>
      <span className="spectrum-Menu-itemLabel">{children}</span>
    </Element>
  );
};

const Section = ({ children, title }) => {
  const id = nextId();

  return (
    <li role="presentation">
      <span className="spectrum-Menu-sectionHeading" id={id} aria-hidden="true">
        {title}
      </span>
      <ul className="spectrum-Menu" role="group" aria-labelledby={id}>
        {children}
      </ul>
    </li>
  );
};

Item.propTypes = {
  isHighlighted: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDivider: PropTypes.bool,
  href: PropTypes.string
};

Section.propTypes = {
  title: PropTypes.string
};

export { Menu, Item, Section };
